import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDate, daysBetween, relativeDue, formatApiError } from "@/lib/utils2";
import { CalendarClock, Check, Trash2, Pencil, X, FolderKanban, Plus } from "lucide-react";
import { toast } from "sonner";
import EditDateButton from "@/components/EditDateButton";
import EditScheduleDialog from "@/components/EditScheduleDialog";
import AddTaskDialog from "@/components/AddTaskDialog";

export default function Schedule() {
  const [items, setItems] = useState([]);
  const [equipmentMap, setEquipmentMap] = useState({});
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("upcoming");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected(new Set(items.map((i) => i.schedule_id)));
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/schedule/bulk-delete", { schedule_ids: ids });
      toast.success(`Deleted ${data.deleted} task${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bulkComplete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/schedule/bulk-complete", { schedule_ids: ids });
      toast.success(`Marked ${data.completed} done`);
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, e, p] = await Promise.all([
        api.get("/schedule", { params: { completed: filter === "completed" } }),
        api.get("/equipment"),
        api.get("/projects"),
      ]);
      const map = Object.fromEntries(e.data.items.map((x) => [x.equipment_id, x]));
      setEquipmentMap(map);
      setProjects(p.data.items || []);
      let arr = s.data.items;
      if (filter === "overdue") {
        arr = arr.filter((x) => daysBetween(x.due_date) < 0);
      } else if (filter === "upcoming") {
        arr = arr.filter((x) => !x.completed);
      }
      setItems(arr);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const projectMap = Object.fromEntries(projects.map((p) => [p.project_id, p]));

  const assignProject = async (scheduleId, projectId) => {
    try {
      await api.put(`/schedule/${scheduleId}`, { project_id: projectId || "" });
      toast.success(projectId ? `Moved to ${projectMap[projectId]?.name || "project"}` : "Removed from project");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bulkAssignProject = async (projectId) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/schedule/bulk-assign-project", {
        schedule_ids: ids,
        project_id: projectId || null,
      });
      toast.success(
        projectId
          ? `Moved ${data.updated} task${data.updated === 1 ? "" : "s"} to ${projectMap[projectId]?.name || "project"}`
          : `Removed ${data.updated} task${data.updated === 1 ? "" : "s"} from project`,
      );
      clearSelection();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onSnooze = async (id, days) => {
    await api.post(`/schedule/${id}/snooze`, { days });
    toast.success(`Snoozed ${days}d`);
    load();
  };

  const onComplete = async (id) => {
    await api.post(`/schedule/${id}/complete`);
    toast.success("Completed");
    load();
  };

  const onDelete = async (id) => {
    await api.delete(`/schedule/${id}`);
    load();
  };

  // Group by due bucket
  const groups = { Overdue: [], "This week": [], "This month": [], Later: [] };
  items.forEach((it) => {
    const d = daysBetween(it.due_date);
    if (d === null) return;
    if (d < 0) groups.Overdue.push(it);
    else if (d <= 7) groups["This week"].push(it);
    else if (d <= 30) groups["This month"].push(it);
    else groups.Later.push(it);
  });

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// Work orders</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Schedule</h1>
          <p className="text-zinc-400 mt-2">{items.length} task{items.length === 1 ? "" : "s"} in view.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm p-1">
              <TabsTrigger value="upcoming" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="filter-upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="overdue" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="filter-overdue">Overdue</TabsTrigger>
              <TabsTrigger value="completed" className="rounded-sm data-[state=active]:bg-orange-600 uppercase tracking-widest text-xs" data-testid="filter-completed">Completed</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button
            onClick={() => setShowAdd(true)}
            className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold"
            data-testid="new-task-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> New task
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="sticky top-0 z-30 mb-4 bg-zinc-900 border border-orange-600 rounded-sm p-3 flex flex-col sm:flex-row sm:items-center gap-3 shadow-lg" data-testid="bulk-actions-bar">
          <div className="flex-1 text-sm">
            <span className="font-display font-bold text-orange-500">{selected.size}</span>
            <span className="text-zinc-400"> task{selected.size === 1 ? "" : "s"} selected</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="ghost" onClick={selectAllVisible} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-orange-500">Select all visible</Button>
            {projects.length > 0 && (
              <Select onValueChange={(v) => bulkAssignProject(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 bg-zinc-800 border border-zinc-700 hover:border-orange-600 rounded-sm text-xs uppercase tracking-widest font-bold w-auto min-w-[160px]" data-testid="bulk-move-to-project">
                  <FolderKanban className="w-3 h-3 mr-1 text-orange-500" />
                  <SelectValue placeholder="Move to project" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">Remove from project</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button size="sm" onClick={bulkComplete} className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-orange-600 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="bulk-complete">
              <Check className="w-3 h-3 mr-1" /> Mark done
            </Button>
            <Button size="sm" onClick={() => setConfirmDelete(true)} className="bg-red-600 hover:bg-red-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="bulk-delete">
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection} className="text-zinc-400 hover:text-white">
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-12 text-center">
          <CalendarClock className="h-12 w-12 mx-auto text-zinc-700 mb-4" />
          <div className="text-zinc-400 mb-4">No tasks in this view.</div>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button onClick={() => setShowAdd(true)} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="new-task-empty-btn">
              <Plus className="w-4 h-4 mr-1" /> New task
            </Button>
            <Link to="/app/equipment/new"><Button variant="secondary" className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-sm uppercase tracking-widest text-xs font-bold">Add equipment</Button></Link>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groups).map(([name, arr]) => arr.length > 0 && (
            <section key={name} className="bg-zinc-900 border border-zinc-800 rounded-sm">
              <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
                <h2 className="font-display font-bold tracking-tight text-lg">{name}</h2>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">{arr.length}</span>
              </div>
              <ul className="divide-y divide-zinc-800">
                {arr.map((it) => {
                  const eq = equipmentMap[it.equipment_id];
                  const d = daysBetween(it.due_date);
                  const overdue = d !== null && d < 0;
                  return (
                    <li key={it.schedule_id} className={`px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 ${selected.has(it.schedule_id) ? "bg-orange-950/20" : ""}`} data-testid={`schedule-item-${it.schedule_id}`}>
                      <Checkbox
                        checked={selected.has(it.schedule_id)}
                        onCheckedChange={() => toggleSelect(it.schedule_id)}
                        className="border-zinc-700 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600 shrink-0"
                        data-testid={`select-task-${it.schedule_id}`}
                        aria-label={`Select ${it.task}`}
                      />
                      <div className={`hidden md:block w-1 h-10 ${overdue ? "bg-red-500" : d <= 7 ? "bg-orange-500" : "bg-zinc-700"}`} />
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => setEditing(it)}
                          className="font-medium text-left hover:text-orange-500 transition-colors flex items-center gap-2 group"
                          data-testid={`edit-task-${it.schedule_id}`}
                        >
                          {it.task}
                          <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-orange-500" />
                        </button>
                        <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          {eq ? (
                            <Link to={`/app/equipment/${eq.equipment_id}`} className="hover:text-orange-400">{eq.name}</Link>
                          ) : "Equipment"}
                          <span className="text-zinc-700">·</span>
                          <EditDateButton
                            scheduleId={it.schedule_id}
                            currentDate={it.due_date}
                            onUpdated={load}
                            className="font-mono text-zinc-500"
                          >
                            {formatDate(it.due_date)}
                          </EditDateButton>
                          <span className="text-zinc-700">·</span>
                          <Select
                            value={it.project_id || "none"}
                            onValueChange={(v) => assignProject(it.schedule_id, v === "none" ? "" : v)}
                          >
                            <SelectTrigger
                              className={`h-6 text-[10px] uppercase tracking-widest px-2 py-0 border rounded-sm bg-transparent gap-1 w-auto min-w-0 ${
                                it.project_id
                                  ? "border-orange-600/40 text-orange-500 hover:bg-orange-950/30"
                                  : "border-zinc-800 text-zinc-500 hover:text-orange-500 hover:border-orange-600/40"
                              }`}
                              data-testid={`row-project-${it.schedule_id}`}
                            >
                              <FolderKanban className="w-3 h-3" />
                              <SelectValue placeholder="Add project" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              <SelectItem value="none">No project</SelectItem>
                              {projects.map((p) => (
                                <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className={`text-sm font-mono ${overdue ? "text-red-400" : d <= 7 ? "text-orange-400" : "text-zinc-300"}`}>
                        {relativeDue(it.due_date)}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => onSnooze(it.schedule_id, 7)} className="text-xs uppercase tracking-widest hover:text-orange-500">+1w</Button>
                        <Button size="sm" variant="ghost" onClick={() => onSnooze(it.schedule_id, 30)} className="text-xs uppercase tracking-widest hover:text-orange-500">+30d</Button>
                        <Button size="sm" variant="ghost" onClick={() => onSnooze(it.schedule_id, 90)} className="text-xs uppercase tracking-widest hover:text-orange-500">+90d</Button>
                        <Button size="sm" onClick={() => onComplete(it.schedule_id)} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid={`schedule-complete-${it.schedule_id}`}>
                          <Check className="w-3 h-3 mr-1" /> Done
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDelete(it.schedule_id)} className="text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
      {editing && (
        <EditScheduleDialog
          schedule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <AddTaskDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSaved={load}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-black text-2xl tracking-tight">Delete tasks?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete <span className="text-orange-500 font-bold">{selected.size}</span> scheduled task{selected.size === 1 ? "" : "s"}. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 hover:bg-zinc-700 border-zinc-700 rounded-sm text-zinc-100">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => { await bulkDelete(); setConfirmDelete(false); }}
              className="bg-red-600 hover:bg-red-500 rounded-sm uppercase tracking-widest text-xs font-bold"
              data-testid="bulk-delete-confirm"
            >
              Delete {selected.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
